import {ref} from 'vue'
import {invoke} from '@tauri-apps/api/core'
import {generateKitchenReceipt, generateReceipt, generateUpdatedItemTicket} from '~/utils/receiptGenerator'


// State is shared across all components using this composable (singleton pattern)
const configuredPrinters = ref([])  // Printers the user has configured in settings (from DB)
const printers = ref([])             // Available printers detected from the system
const loadingPrinters = ref(false)   // True while scanning for printers
const error = ref(null)               // Last error message (if any)
const printing = ref(false)           // True while actively printing

export const usePrinter = () => {
    const db = usePosDatabase()

    // Scans the system for ESC/POS compatible printers (USB, Network, Bluetooth)
    // Returns the best default printer name based on priority: USB > Network > Bluetooth > Other
    // Called on app startup and when user refreshes printer list in settings
    const listPrinters = async () => {
        loadingPrinters.value = true
        error.value = null
        try {
            // Try the main enumeration method first (faster, more reliable)
            let result = await invoke('list_printers_windows')

            // If no printers found, try WMI as a fallback (slower but more comprehensive)
            // WMI can sometimes find printers that the main method misses
            if (result.length === 0) {
                console.log('No printers found with standard method, trying WMI fallback...')
                try {
                    result = await invoke('list_all_printers_wmi')
                } catch (wmiError) {
                    console.warn('WMI fallback also failed:', wmiError)
                }
            }

            printers.value = result

            // Log what we found for debugging
            if (result.length > 0) {
                console.log(`Found ${result.length} ESC/POS compatible printers:`)
                result.forEach((printer, index) => {
                    console.log(`  ${index + 1}. ${printer.name}`)
                    console.log(`     Port: ${printer.port_name}`)
                    console.log(`     Type: ${printer.printer_type}`)  // USB, Network, Bluetooth, etc.
                    console.log('     ---')
                })
            } else {
                console.log('No ESC/POS compatible printers found')
            }

            // Smart default printer selection
            // Why this priority? USB is most reliable, Network is convenient, Bluetooth can be flaky
            const getDefaultPrinter = printers => {
                if (printers.length === 0) return null

                // Prefer USB printers (most reliable connection)
                const usbPrinter = printers.find(p => p.printer_type === 'USB')
                if (usbPrinter) return usbPrinter.name

                // Then network printers (good for multi-terminal setups)
                const networkPrinter = printers.find(p => p.printer_type === 'Network')
                if (networkPrinter) return networkPrinter.name

                // Then Bluetooth printers (portable but can disconnect)
                const bluetoothPrinter = printers.find(p => p.printer_type === 'Bluetooth')
                if (bluetoothPrinter) return bluetoothPrinter.name

                // Fallback to whatever we found
                return printers[0].name
            }

            const defaultPrinter = getDefaultPrinter(result)

            if (defaultPrinter) {
                console.log(`Selected default printer: ${defaultPrinter}`)
            }

            return defaultPrinter
        } catch (e) {
            error.value = `Critical error during printer enumeration: ${e}`
            printers.value = [] // Clear list on error
            console.error('Critical printer enumeration error:', e)

            return null
        } finally {
            loadingPrinters.value = false
        }
    }

    // Helper functions for filtering printers by connection type
    // Useful in UI to show "USB Printers", "Network Printers" separately
    const getPrintersByType = type => {
        return printers.value.filter(printer => printer.printer_type === type)
    }

    // Quick checks for printer availability (used in UI to show/hide options)
    const hasBluetoothPrinters = () => getPrintersByType('Bluetooth').length > 0
    const hasNetworkPrinters = () => getPrintersByType('Network').length > 0
    const hasUSBPrinters = () => getPrintersByType('USB').length > 0

    // Manual refresh (called when user clicks "Refresh Printers" in settings)
    const refreshPrinters = async () => {
        console.log('Refreshing printer list...')
        return await listPrinters()
    }

    // Loads printers that the user has set up in settings
    // Each configured printer has: friendly_name, system_name, destination (Kitchen/Receipt/Bar), is_enabled
    // Called on app startup and after saving printer configuration
    const loadConfiguredPrinters = async () => {
        await db.initializeDatabase() // Make sure DB is ready first
        configuredPrinters.value = await db.getConfiguredPrinters()
        console.log("Configured printers: ", configuredPrinters.value)
    }


    // Master print function - routes print jobs to the right printer based on destination
    // How it works:
    // 1. Look up configured printer for this destination (Kitchen, Receipt, Bar)
    // 2. Generate the appropriate receipt format (different for kitchen vs customer)
    // 3. Send raw ESC/POS bytes to the printer via Rust backend
    // Used by: printKitchenReceipt, printIncrementalKitchenReceipt, customer receipt printing
    const printToDestination = async (destination, order, options = {}) => {
        console.log(destination, order, options);
        // Find the printer configured for this destination that's currently enabled
        const printerConfig = configuredPrinters.value.find(
                p => p.destination === destination && p.is_enabled === 'true',
        )

        if (!printerConfig) {
            return {success: false, message: `No enabled printer configured for '${destination}'.`}
        }

        printing.value = true
        error.value = null

        try {
            let rawData
            const config = useRuntimeConfig()

            // Handle manual cash drawer opening (no order)
            if (!order && destination === 'Receipt') {
                rawData = new Uint8Array([0x1B, 0x70, 0x00, 0x19, 0xFA])
            } else if (!order) {
                return {success: false, message: 'No order data provided.'}
            } else {
                // Generate the receipt based on destination type
                // Each generator returns raw ESC/POS command bytes (special printer language)
                switch (destination) {
                    case 'Receipt':
                        // Customer receipt: includes prices, taxes, payment info, business details
                        await new Promise((resolve) => {
                            setTimeout(async () => {
                                rawData = await generateReceipt(order, config)
                                resolve()
                            }, 500)
                        })

                        break
                case 'Kitchen':
                case 'Bar':

                    // Kitchen/Bar receipt: focuses on what to make, includes item notes, no prices
                    await new Promise((resolve) => {
                        setTimeout(() => {
                            rawData = generateKitchenReceipt(order, options.isIncremental, options.newItems, options.isRemoval, options.removedItems)
                            resolve()
                        }, 500)
                    })
                    break
                default:
                    throw new Error(`Unknown print destination: ${destination}`)
                }

                if (options?.test) {
                    rawData = generateMinimalReceipt(order)
                }
            }

            try {
                await invoke('check_printer_hardware', {
                    printerName: printerConfig.system_name,
                })
                console.log(`[Printer] Hardware check passed for ${printerConfig.system_name}`)
            } catch (hardwareError) {
                console.error(`[Printer] Hardware check failed:`, hardwareError)
                throw new Error(`Printer "${printerConfig.display_name}" is not available. Please check if it's powered on and connected.`)
            }

            // Send the raw bytes to the printer via our Rust backend
            // The Rust code handles the low-level Windows printer API calls
            const res = await invoke('print_raw_bytes', {
                printerName: printerConfig.system_name,
                data: Array.from(rawData),
            })

            return {success: true, message: `Successfully sent to ${destination} printer.`}
        } catch (e) {
            const errorString = e.toString().toLowerCase()
            let userFriendlyMessage = `Print to '${destination}' failed: ${e}`

            if (errorString.includes('not available') || errorString.includes('offline') || errorString.includes('not found')) {
                userFriendlyMessage = `Printer "${destination}" is offline or not connected. Please check:\n• Printer is powered on\n• Cables are connected\n• Printer is not in error state (paper jam, no paper, etc.)`
            } else if (errorString.includes('access denied') || errorString.includes('permission')) {
                userFriendlyMessage = `Cannot access printer "${destination}". Please check printer permissions.`
            } else if (errorString.includes('paper') || errorString.includes('jam')) {
                userFriendlyMessage = `Printer "${destination}" has a paper issue. Please check for paper jams or refill paper.`
            } else if (errorString.includes('busy')) {
                userFriendlyMessage = `Printer "${destination}" is busy. Please wait and try again.`
            }

            error.value = userFriendlyMessage
            console.error(`[Printer] ${userFriendlyMessage}`)

            return {success: false, message: userFriendlyMessage}
        } finally {
            printing.value = false
        }
    }

    // Prints a full kitchen receipt when sending an order to the kitchen
    // Includes all items, table number, order type, timestamps
    // Only called from main POS terminal (tablets/KDS don't print)
    const printKitchenReceipt = async (order) => {
        return await printToDestination('Kitchen', order, {isIncremental: false})
    }

    // Prints a customer receipt with full order details, prices, taxes, payment info
    // Used for completed orders (pickup, delivery, dine-in checkout)
    const printCustomerReceipt = async (order) => {
        return await printToDestination('Receipt', order, {})
    }

    // Prints just the newly added items (not the whole order again)
    // Used when customers order more food after their initial order was sent
    // Shows "ADDITIONAL ITEMS" header to distinguish from original order
    const printIncrementalKitchenReceipt = async (order, newItems) => {
        if (!newItems || newItems.length === 0) {
            return {success: false, message: 'No new items to print'}
        }

        console.log('[PrintIncremental] Called with:', {
            orderNumber: order.orderNumber,
            newItemsCount: newItems.length,
            newItems: newItems.map(i => ({ name: i.menuItemName, qty: i.quantity }))
        })

        return await printToDestination('Kitchen', order, {
            isIncremental: true,  // Tells the generator to format as "additional items"
            newItems: newItems    // Only these items get printed, not the full order
        })
    }

    const printRemovedItemsKitchenReceipt = async (order, removedItems) => {
        if (!removedItems || removedItems.length === 0) {
            return {success: false, message: 'No removed items to print'}
        }

        return await printToDestination('Kitchen', order, {
            isRemoval: true,        // Tells the generator to format as "REMOVED items"
            removedItems: removedItems  // Items that were cancelled/removed
        })
    }

    // Checks if this device should print receipts
    // Why? In a multi-terminal setup, only the main POS prints to avoid duplicate receipts
    // Tablets, KDS displays, and Kiosks receive orders via socket sync but don't print
    // Returns true if this is the main POS (not in tablet, KDS, or kiosk mode)
    //
    // Source of truth, in order:
    //   1. Tauri get_device_type — set by the build target. Authoritative when
    //      present: a `com.starvly.tablet` build is a tablet regardless of any
    //      SQLite settings, even on first launch before the user has visited
    //      Settings. Without this check, a fresh tablet defaulted to "main POS"
    //      and started printing duplicate kitchen receipts to whatever printer
    //      the network discovered.
    //   2. SQLite settings — manual overrides for the unified `com.starvly.dev`
    //      build that ships POS/KDS/Tablet/Kiosk in one binary.
    //   3. Errors fall back to true so a sole-POS install keeps printing.
    const isMainPosTerminal = async () => {
        try {
            try {
                const { invoke } = await import('@tauri-apps/api/core')
                const deviceType = await invoke('get_device_type')
                if (deviceType === 'tablet' || deviceType === 'kds' || deviceType === 'kiosk') {
                    console.log('[usePrinter] isMainPosTerminal: Tauri device_type =', deviceType, '→ not main')
                    return false
                }
            } catch {
                // Not Tauri or device_type not set — fall through to SQLite check.
            }

            const isTabletMode = await db.getPosSetting('isTabletMode')
            const isKdsMode = await db.getPosSetting('isKdsMode')
            const isKioskMode = await db.getPosSetting('isKioskMode')

            const isMain = isTabletMode !== '1' && isKdsMode !== '1' && isKioskMode !== '1'
            console.log('[usePrinter] isMainPosTerminal:', { isTabletMode, isKdsMode, isKioskMode, isMain })
            return isMain
        } catch (err) {
            console.error('[usePrinter] Failed to check POS mode:', err)
            return true // Default to printing if we can't determine mode (safer for business)
        }
    }

    const printUpdatedItemTicket = async (order, updatedItem) => {
        const printerConfig = configuredPrinters.value.find(
            p => p.destination === 'Kitchen' && p.is_enabled === 'true',
        )

        if (!printerConfig) {
            return {success: false, message: 'No enabled kitchen printer configured.'}
        }

        printing.value = true
        error.value = null

        try {
            const rawData = generateUpdatedItemTicket(order, updatedItem)

            try {
                await invoke('check_printer_hardware', {
                    printerName: printerConfig.system_name,
                })
            } catch (hardwareError) {
                throw new Error(`Printer "${printerConfig.display_name}" is not available.`)
            }

            await invoke('print_raw_bytes', {
                printerName: printerConfig.system_name,
                data: Array.from(rawData),
            })

            console.log('[Printer] Updated item ticket printed successfully')
            return {success: true}
        } catch (err) {
            console.error('[Printer] Updated item ticket print error:', err)
            error.value = err.message || 'Failed to print updated ticket'
            return {success: false, message: error.value}
        } finally {
            printing.value = false
        }
    }


    const printTerminalReceipt = async (receiptText, options = {}) => {
        if (!receiptText) {
            return {success: false, message: 'No receipt text provided'}
        }

        let printerSystemName = options.printerName || null

        if (!printerSystemName) {
            const printerConfig = configuredPrinters.value.find(
                p => p.destination === 'Receipt' && p.is_enabled === 'true',
            )

            if (!printerConfig) {
                console.warn('[Printer] No receipt printer configured for terminal receipt')
                return {success: false, message: 'No receipt printer configured'}
            }
            printerSystemName = printerConfig.system_name
        }

        printing.value = true
        error.value = null

        try {
            const encoder = new TextEncoder()
            const textBytes = encoder.encode(receiptText)

            const ESC = 0x1B
            const GS = 0x1D
            const commands = []

            commands.push(ESC, 0x40)
            commands.push(ESC, 0x61, 0x01)

            if (options.title) {
                commands.push(ESC, 0x21, 0x30)
                const titleBytes = encoder.encode(options.title + '\n')
                commands.push(...titleBytes)
                commands.push(ESC, 0x21, 0x00)
                commands.push(...encoder.encode('\n'))
            }

            commands.push(...textBytes)

            if (options.signatureLine) {
                commands.push(...encoder.encode('\n\n'))
                commands.push(...encoder.encode('SIGNATURE: ________________________\n'))
                commands.push(...encoder.encode('\n'))
            }

            commands.push(...encoder.encode('\n\n\n\n\n'))
            commands.push(GS, 0x56, 0x00)

            if (options.openCashDrawer) {
                commands.push(ESC, 0x70, 0x00, 0x19, 0xFA)
            }

            const rawData = new Uint8Array(commands)

            await invoke('print_raw_bytes', {
                printerName: printerSystemName,
                data: Array.from(rawData),
            })

            console.log('[Printer] Terminal receipt printed successfully')
            return {success: true}
        } catch (err) {
            console.error('[Printer] Terminal receipt print error:', err)
            error.value = err.message || 'Failed to print terminal receipt'
            return {success: false, message: error.value}
        } finally {
            printing.value = false
        }
    }

    const printKioskReceipt = async (printerName, order, terminalReceipt = null) => {
        if (!printerName) {
            return {success: false, message: 'No printer name provided'}
        }

        printing.value = true
        error.value = null

        try {
            const encoder = new TextEncoder()
            const ESC = 0x1B
            const GS = 0x1D
            const commands = []

            commands.push(ESC, 0x40)

            commands.push(ESC, 0x61, 0x01)
            commands.push(ESC, 0x21, 0x30)
            commands.push(...encoder.encode('KIOSK ORDER\n'))
            commands.push(ESC, 0x21, 0x00)
            commands.push(...encoder.encode('\n'))

            commands.push(ESC, 0x21, 0x10)
            commands.push(...encoder.encode(`#${order.orderNumber}\n`))
            commands.push(ESC, 0x21, 0x00)
            commands.push(...encoder.encode('\n'))

            const orderType = order.orderType === 'dine_in' ? 'Dine In' : 'Takeaway'
            commands.push(...encoder.encode(`${orderType}\n`))
            commands.push(...encoder.encode(`${new Date(order.createdAt).toLocaleString()}\n`))
            commands.push(...encoder.encode('--------------------------------\n'))

            if (order.items && order.items.length > 0) {
                for (const item of order.items) {
                    commands.push(...encoder.encode(`${item.quantity}x ${item.name}\n`))
                    const itemTotal = (item.unitPrice * item.quantity).toFixed(2)
                    commands.push(...encoder.encode(`   $${itemTotal}\n`))

                    if (item.modifiers && item.modifiers.length > 0) {
                        for (const mod of item.modifiers) {
                            const modName = mod.modifierOptionName || mod.name
                            if (modName) {
                                commands.push(...encoder.encode(`   + ${modName}\n`))
                            }
                        }
                    }
                }
            }

            commands.push(...encoder.encode('--------------------------------\n'))
            commands.push(...encoder.encode(`Subtotal:        $${Number(order.subtotal).toFixed(2)}\n`))

            if (order.serviceFee && order.serviceFee > 0) {
                commands.push(...encoder.encode(`Service Fee:     $${Number(order.serviceFee).toFixed(2)}\n`))
            }

            if (order.orderSurcharge && order.orderSurcharge > 0) {
                commands.push(...encoder.encode(`Surcharge:       $${Number(order.orderSurcharge).toFixed(2)}\n`))
            }

            if (order.surcharge && order.surcharge > 0) {
                commands.push(...encoder.encode(`Card Fee:        $${Number(order.surcharge).toFixed(2)}\n`))
            }

            commands.push(...encoder.encode(`GST:             $${Number(order.taxAmount).toFixed(2)}\n`))
            commands.push(ESC, 0x21, 0x10)
            commands.push(...encoder.encode(`TOTAL:           $${Number(order.total).toFixed(2)}\n`))
            commands.push(ESC, 0x21, 0x00)

            commands.push(...encoder.encode('--------------------------------\n'))
            commands.push(...encoder.encode(`Paid by: ${order.cardType || 'Card'} ****${order.last4 || '****'}\n`))

            if (order.customerName) {
                commands.push(...encoder.encode(`Customer: ${order.customerName}\n`))
            }

            commands.push(...encoder.encode('\n'))
            commands.push(ESC, 0x61, 0x01)
            commands.push(...encoder.encode('Thank you for your order!\n'))

            if (terminalReceipt) {
                commands.push(...encoder.encode('\n'))
                commands.push(...encoder.encode('================================\n'))
                commands.push(ESC, 0x61, 0x01)
                commands.push(ESC, 0x21, 0x30)
                commands.push(...encoder.encode('EFTPOS RECEIPT\n'))
                commands.push(ESC, 0x21, 0x00)
                commands.push(...encoder.encode('================================\n'))
                commands.push(ESC, 0x61, 0x00)
                commands.push(...encoder.encode(terminalReceipt))
                commands.push(...encoder.encode('\n'))
            }

            commands.push(...encoder.encode('\n\n\n\n'))

            commands.push(GS, 0x56, 0x00)

            const rawData = new Uint8Array(commands)

            await invoke('print_raw_bytes', {
                printerName: printerName,
                data: Array.from(rawData),
            })

            console.log('[Printer] Kiosk receipt printed successfully to:', printerName)
            return {success: true}
        } catch (err) {
            console.error('[Printer] Kiosk receipt print error:', err)
            error.value = err.message || 'Failed to print kiosk receipt'
            return {success: false, message: error.value}
        } finally {
            printing.value = false
        }
    }

    // What we export to other components
    return {
        // Reactive state
        printers,                          // Available printers from system scan
        loadingPrinters,                   // True while scanning for printers
        printing,                          // True while actively printing
        error,                             // Last error message
        configuredPrinters,                // User-configured printers from DB

        // Methods
        listPrinters,                      // Scan system for printers
        loadConfiguredPrinters,            // Load user settings from DB
        printToDestination,                // Core print function (routes by destination)
        printKitchenReceipt,               // Print full kitchen receipt
        printCustomerReceipt,              // Print customer receipt with prices/taxes
        printIncrementalKitchenReceipt,    // Print only new items
        printRemovedItemsKitchenReceipt,   // Print removed items with REMOVED notation
        printUpdatedItemTicket,            // Print ticket for item with updated notes
        printTerminalReceipt,              // Print terminal payment receipt (Tyro, etc.)
        printKioskReceipt,                 // Print kiosk receipt directly to named printer
        isMainPosTerminal,                 // Check if this device should print
    }
}
